package com.eladfinish.bible.contest.platform.text.mappers

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import com.eladfinish.bible.contest.extensions.FONT_SERIF
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP

class SpannableStringKmpToAnnotatedStringMapper {

    data class Output(
        val annotatedString: AnnotatedString,
        val actionsById: Map<String, ClickAction>,
    )

    operator fun invoke(
        model: SpannableStringKMP,
        baseTextSize: TextUnit,
    ): Output {
        val raw = model.charSequence.toString()

        // click annotations can't store objects; store ids -> actions map
        val actionsById = linkedMapOf<String, ClickAction>()
        var nextId = 0

        val annotated = buildAnnotatedString {
            append(raw)

            model.spannedRangeList.forEach { range ->
                val start = range.textRange.start
                val end = range.textRange.end
                if (start < 0 || end > raw.length || start >= end) return@forEach

                when (val type = range.spanType) {
                    is SpanType.ForegroundColor -> {
                        addStyle(
                            SpanStyle(color = type.color),
                            start,
                            end,
                        )
                    }

                    SpanType.Bold -> {
                        addStyle(
                            SpanStyle(fontWeight = FontWeight.Bold),
                            start,
                            end,
                        )
                    }

                    SpanType.Underline -> {
                        addStyle(
                            SpanStyle(textDecoration = TextDecoration.Underline),
                            start,
                            end,
                        )
                    }

                    is SpanType.RelativeSize -> {
                        // ot pasuk scaling (e.g. 0.5f)
                        addStyle(
                            SpanStyle(fontSize = baseTextSize * type.proportion),
                            start,
                            end,
                        )
                    }

                    is SpanType.CustomTypeface -> {
                        // Keep it simple: map your FONT_SERIF to FontFamily.Serif.
                        // If later you want per-font-family loading -> provide a resolver.
                        val family =
                            if (type.family == FONT_SERIF) FontFamily.Serif else FontFamily.Default

                        addStyle(
                            SpanStyle(fontFamily = family, fontStyle = FontStyle.Normal),
                            start,
                            end,
                        )
                    }

                    is SpanType.BackgroundColor -> {
                        addStyle(
                            SpanStyle(background = type.color),
                            start,
                            end,
                        )
                    }

                    is SpanType.BackgroundColorFromPalette -> {
                        // If you still need palette mapping, handle here (but it was Android-specific before).
                        // For now, you can ignore or inject a palette resolver.
                    }

                    is SpanType.Clickable -> {
                        val id = (nextId++).toString()
                        actionsById[id] = type.action

                        // mark clickable range
                        addStringAnnotation(
                            tag = TAG_CLICK,
                            annotation = id,
                            start = start,
                            end = end,
                        )

                        // you usually also want clickable ranges to look like links or just keep color
                        // here we keep underline off; your blanks are underscores anyway.
                    }

                    is SpanType.ClickableLegacy -> Unit
                }
            }
        }

        return Output(
            annotatedString = annotated,
            actionsById = actionsById,
        )
    }

    companion object {
        const val TAG_CLICK = "click_action"
    }
}
